// pages/Notifications.jsx
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FiBell, FiCheck, FiRefreshCw, FiAtSign, FiMessageCircle, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { fetchNotifications, markAsRead } from '../api/notificationApi';
import { useSocket } from '../context/SocketContext';
import '../styles/Notifications.css';
import Header from '../components/header/header';
import Navvbar from '../components/header/Navvbar';

const TYPE_META = {
  case_mention: { label: 'Case mention', Icon: FiAtSign },
  group_message: { label: 'Group message', Icon: FiMessageCircle }
};

const getTypeMeta = (type) => TYPE_META[type] || { label: type || 'Notification', Icon: FiBell };

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
  return new Date(t).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const { setNotifications, setUnreadCount } = useSocket();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotifications({ limit: 200 });
      if (data.success) {
        setItems(data.notifications || []);
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      } else setError(data.message || 'Failed to load');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const handleRead = async (n) => {
    if (!n.read) {
      try {
        await markAsRead(n._id);
        setItems((prev) =>
          prev.map((x) => (x._id === n._id ? { ...x, read: true } : x))
        );
      } catch {}
    }
  };

  const handleOpen = async (n) => {
    await handleRead(n);
    if (n.type === 'case_mention' && n.data?.caseNumber) {
      navigate(`/case/${n.data.caseNumber}?noteId=${n.data.noteId || ''}`);
    } else if (n.type === 'group_message') {
      navigate('/chat');
    }
  };

  const unreadItems = items.filter((n) => !n.read);
  const visibleItems = filter === 'unread' ? unreadItems : items;

  return (
    <>
    <Header/>
    <Navvbar/>
    <div className="nt-page">
      <div className="nt-inner">
        <div className="nt-header">
          <div className="nt-heading">
            <span className="nt-heading-icon"><FiBell /></span>
            <div>
              <h4 className="nt-title">Notifications</h4>
              <p className="nt-subtitle">
                {loading
                  ? 'Loading…'
                  : unreadItems.length > 0
                    ? `You have ${unreadItems.length} unread notification${unreadItems.length > 1 ? 's' : ''}`
                    : "You're all caught up"}
              </p>
            </div>
          </div>
          <button type="button" className="nt-refresh" onClick={load} disabled={loading}>
            <FiRefreshCw size={15} className={loading ? 'nt-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>

        {!loading && !error && items.length > 0 && (
          <div className="nt-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'all'}
              className={`nt-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All <span className="nt-tab-count">{items.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'unread'}
              className={`nt-tab ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread <span className="nt-tab-count">{unreadItems.length}</span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : items.length === 0 ? (
          <div className="nt-panel">
            <div className="nt-state">
              <span className="nt-state-icon"><FiBell /></span>
              <strong>No notifications</strong>
              <span>New activity will show up here.</span>
            </div>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="nt-panel">
            <div className="nt-state">
              <span className="nt-state-icon"><FiCheckCircle /></span>
              <strong>You're all caught up</strong>
              <span>There are no unread notifications.</span>
            </div>
          </div>
        ) : (
          <div className="nt-panel">
            {visibleItems.map((n) => {
              const { label, Icon } = getTypeMeta(n.type);
              const canOpen = n.type === 'case_mention' || n.type === 'group_message';
              const fullDate = n.createdAt ? new Date(n.createdAt).toLocaleString() : '';

              return (
                <div key={n._id} className={`nt-item ${!n.read ? 'unread' : ''}`}>
                  <span className="nt-icon"><Icon /></span>

                  <div className="nt-body">
                    <div className="nt-item-head">
                      <span className="nt-item-title">{n.title}</span>
                      {!n.read && <span className="nt-new">New</span>}
                    </div>
                    {n.message && <div className="nt-message">{n.message}</div>}
                    <div className="nt-meta">
                      <span className="nt-type">{label}</span>
                      <span title={fullDate}>{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>

                  {(canOpen || !n.read) && (
                    <div className="nt-actions">
                      {canOpen && (
                        <button type="button" className="nt-btn primary" onClick={() => handleOpen(n)}>
                          Open
                        </button>
                      )}
                      {!n.read && (
                        <button type="button" className="nt-btn ghost" onClick={() => handleRead(n)}>
                          <FiCheck size={14} /> Mark read
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </>
  );
}